import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { CARD_LIMITS, RARITY_CONFIG, RARITY, REQUIREMENT_TYPE, ACTION_TYPE } from '../data/cards';
import { CARD_POOL, STARTER_CARDS } from '../data/cardTemplates';
import { HOUSING_TIERS } from '../data/housing';

const getTodayDate = () => new Date().toISOString().split('T')[0];

// Generate unique ID for deposits and cards
export const generateId = () => Date.now().toString(36) + Math.random().toString(36).substr(2);

// Select random rarity based on weights (60/25/12/3)
const selectRandomRarity = () => {
  const roll = Math.floor(Math.random() * 100);
  // Cumulative thresholds: 0-59 common, 60-84 rare, 85-96 epic, 97-99 legendary
  if (roll < 60) return RARITY.COMMON;
  if (roll < 85) return RARITY.RARE;
  if (roll < 97) return RARITY.EPIC;
  return RARITY.LEGENDARY;
};

// Create card instance from template
const createCardInstance = (template, gameDate) => {
  const expDate = new Date(gameDate);
  expDate.setDate(expDate.getDate() + RARITY_CONFIG[template.rarity].expirationDays);

  return {
    id: generateId(),
    cardId: template.cardId,
    name: template.name,
    rarity: template.rarity,
    description: template.description,
    requirement: template.requirement,
    reward: template.reward,
    expirationDate: expDate.toISOString().split('T')[0],
    createdDate: gameDate,  // For streak tracking
    progress: 0,
    isActive: false,
    streakBroken: false,    // For no-withdrawal streak cards
    lastLoginDate: null,    // For login streak tracking
    depositDays: []         // For uniqueDays tracking (Pay Yourself First)
  };
};

export const useGameStore = create(
  persist(
    (set, get) => ({
      // Core state
      gameDate: getTodayDate(),
      lastRealDate: getTodayDate(),

      // M Economy state
      mBalance: 0,
      deposits: [],          // Array of { id, amount, mEarned, date }
      lastLoginDate: null,   // For daily bonus tracking

      // Simulated Accounts (Phase 6)
      accounts: {
        HYSA: 0,
        fourOhOneK: 0,
        brokerage: 0
      },

      // Housing (Phase 5)
      housingLevel: 0,

      // Card System state (Phase 3)
      cards: [],                    // Array of card instances (both active and vault)
      lastDrawDate: null,           // For daily draw tracking
      streakStartDate: null,        // For milestone tracking (set on first deposit)
      lastMilestoneChecks: {        // Milestone draw tracking
        day7: false,
        day30: false,
        day365: false,
        savings100: false,
        savings500: false,
        savings1000: false,
        savings5000: false
      },

      // Card System helper getters
      getActiveCards: () => get().cards.filter(c => c.isActive),
      getVaultCards: () => get().cards.filter(c => !c.isActive),

      // Card System Actions
      addCard: (cardInstance) => {
        const state = get();
        const activeCount = state.cards.filter(c => c.isActive).length;
        const vaultCount = state.cards.filter(c => !c.isActive).length;

        // Check if vault is full when card would go to vault
        if (activeCount >= CARD_LIMITS.ACTIVE_SLOTS && vaultCount >= CARD_LIMITS.VAULT_CAPACITY) {
          return { added: false, reason: 'vault_full' };
        }

        // Determine if card goes to active or vault
        const isActive = activeCount < CARD_LIMITS.ACTIVE_SLOTS;
        const newCard = { ...cardInstance, isActive };

        set({ cards: [...state.cards, newCard] });
        return { added: true, isActive };
      },

      activateCard: (cardId) => {
        const state = get();
        const card = state.cards.find(c => c.id === cardId);

        if (!card || card.isActive) {
          return false; // Card not found or already active
        }

        const activeCount = state.cards.filter(c => c.isActive).length;
        if (activeCount >= CARD_LIMITS.ACTIVE_SLOTS) {
          return false; // Active slots full
        }

        set({
          cards: state.cards.map(c =>
            c.id === cardId ? { ...c, isActive: true } : c
          )
        });
        return true;
      },

      deactivateCard: (cardId) => {
        const state = get();
        const card = state.cards.find(c => c.id === cardId);

        if (!card || !card.isActive) {
          return false; // Card not found or not active
        }

        const vaultCount = state.cards.filter(c => !c.isActive).length;
        if (vaultCount >= CARD_LIMITS.VAULT_CAPACITY) {
          return false; // Vault is full
        }

        set({
          cards: state.cards.map(c =>
            c.id === cardId ? { ...c, isActive: false } : c
          )
        });
        return true;
      },

      removeCard: (cardId) => {
        const state = get();
        const card = state.cards.find(c => c.id === cardId);

        if (!card) {
          return null; // Card not found
        }

        set({ cards: state.cards.filter(c => c.id !== cardId) });
        return card;
      },

      updateCardProgress: (cardId, newProgress) => {
        const state = get();
        const card = state.cards.find(c => c.id === cardId);

        if (!card) {
          return false; // Card not found
        }

        set({
          cards: state.cards.map(c =>
            c.id === cardId ? { ...c, progress: newProgress } : c
          )
        });
        return true;
      },

      // Card Expiration Actions
      processExpiredCards: () => {
        const state = get();
        const currentDate = state.gameDate;
        const expired = [];

        // Find expired cards (expirationDate < currentDate)
        const remainingCards = state.cards.filter(card => {
          if (card.expirationDate < currentDate) {
            expired.push(card);
            return false;
          }
          return true;
        });

        if (expired.length > 0) {
          set({ cards: remainingCards });
        }

        return { expired, count: expired.length };
      },

      getCardTimeRemaining: (cardId) => {
        const state = get();
        const card = state.cards.find(c => c.id === cardId);

        if (!card) {
          return null;
        }

        const gameDate = new Date(state.gameDate);
        const expDate = new Date(card.expirationDate);
        const msPerDay = 24 * 60 * 60 * 1000;
        const daysRemaining = Math.ceil((expDate - gameDate) / msPerDay);

        return daysRemaining;
      },

      // Card Progress Tracking Actions
      incrementCardProgress: (cardId, amount = 1) => {
        const state = get();
        const card = state.cards.find(c => c.id === cardId);

        if (!card) {
          return { success: false, reason: 'not_found' };
        }

        const newProgress = card.progress + amount;
        const target = card.requirement?.target || card.requirement?.count || 0;
        const isComplete = card.requirement?.type !== 'none' && target > 0 && newProgress >= target;

        set({
          cards: state.cards.map(c =>
            c.id === cardId ? { ...c, progress: newProgress } : c
          )
        });

        return { success: true, newProgress, isComplete };
      },

      checkCardCompletion: (cardId) => {
        const state = get();
        const card = state.cards.find(c => c.id === cardId);

        if (!card) {
          return { isComplete: false, progress: 0, target: 0 };
        }

        // Cards with 'none' requirement type can never be completed
        if (card.requirement?.type === 'none') {
          return { isComplete: false, progress: card.progress, target: 0 };
        }

        const target = card.requirement?.target || card.requirement?.count || 0;
        const isComplete = target > 0 && card.progress >= target;

        return { isComplete, progress: card.progress, target };
      },

      completeCard: (cardId) => {
        const state = get();
        const completion = get().checkCardCompletion(cardId);

        if (!completion.isComplete) {
          return { success: false, reason: 'not_complete' };
        }

        const card = state.cards.find(c => c.id === cardId);
        if (!card) {
          return { success: false, reason: 'not_found' };
        }

        let reward = card.reward || 0;
        const cardName = card.name;
        let doublerReward = 0;

        // Check for The Doubler passive effect (only if not completing The Doubler itself)
        const doublerCard = state.cards.find(c =>
          c.isActive &&
          c.id !== cardId &&
          c.requirement?.type === REQUIREMENT_TYPE.PASSIVE &&
          c.requirement?.effect === 'double-reward'
        );

        if (doublerCard) {
          // Double the reward
          reward = reward * 2;
          doublerReward = doublerCard.reward || 0;

          // Remove both the completing card and The Doubler
          set({
            mBalance: state.mBalance + reward + doublerReward,
            cards: state.cards.filter(c => c.id !== cardId && c.id !== doublerCard.id)
          });

          console.log(`The Doubler activated! ${cardName} reward doubled to ${reward}M, Doubler reward: ${doublerReward}M`);

          return {
            success: true,
            reward,
            cardName,
            doublerUsed: true,
            doublerReward,
            totalReward: reward + doublerReward
          };
        }

        // Normal completion - add reward to M balance and remove card
        set({
          mBalance: state.mBalance + reward,
          cards: state.cards.filter(c => c.id !== cardId)
        });

        return { success: true, reward, cardName };
      },

      getCardProgress: (cardId) => {
        const state = get();
        const card = state.cards.find(c => c.id === cardId);

        if (!card) {
          return null;
        }

        const target = card.requirement?.target || card.requirement?.count || 0;
        const percentage = target > 0 ? Math.floor((card.progress / target) * 100) : 0;

        return {
          current: card.progress,
          target,
          percentage: Math.min(percentage, 100) // Cap at 100%
        };
      },

      // Get all completable cards (active cards with completed requirements)
      getCompletableCards: () => {
        const state = get();
        return state.cards.filter(card => {
          if (!card.isActive) return false;
          const target = card.requirement?.target || card.requirement?.count || 0;
          if (target <= 0) return false;
          return card.progress >= target;
        });
      },

      // Process card triggers when game actions occur
      processCardTriggers: (actionType, actionData = {}) => {
        const state = get();
        const gameDate = state.gameDate;
        const updated = [];
        const completable = [];

        // Update cards based on action type (only active cards make progress)
        const updatedCards = state.cards.map(card => {
          if (!card.isActive) return card;

          const req = card.requirement;
          if (!req) return card;

          let newCard = { ...card };
          let wasUpdated = false;

          // Handle based on requirement type
          switch (req.type) {
            case REQUIREMENT_TYPE.ACTION:
              // Action-based cards: increment when action matches
              if (req.action === actionType) {
                // Handle uniqueDays (Pay Yourself First)
                if (req.uniqueDays && actionType === ACTION_TYPE.DEPOSIT) {
                  if (!newCard.depositDays.includes(gameDate)) {
                    newCard.depositDays = [...newCard.depositDays, gameDate];
                    newCard.progress = newCard.depositDays.length;
                    wasUpdated = true;
                  }
                } else {
                  newCard.progress = card.progress + 1;
                  wasUpdated = true;
                }
              }
              break;

            case REQUIREMENT_TYPE.THRESHOLD:
              // Threshold-based: check if amount meets requirement
              if (req.action === actionType && actionData.amount >= req.amount) {
                newCard.progress = req.amount; // Mark as complete
                wasUpdated = true;
              }
              break;

            case REQUIREMENT_TYPE.STREAK:
              // Streak-based: handled differently based on action type
              if (req.action === ACTION_TYPE.LOGIN && actionType === ACTION_TYPE.LOGIN) {
                // Login streak: check if consecutive day
                const lastLogin = newCard.lastLoginDate;
                if (!lastLogin) {
                  // First login for this card
                  newCard.lastLoginDate = gameDate;
                  newCard.progress = 1;
                  wasUpdated = true;
                } else {
                  const lastDate = new Date(lastLogin);
                  const currentDate = new Date(gameDate);
                  const msPerDay = 24 * 60 * 60 * 1000;
                  const daysDiff = Math.floor((currentDate - lastDate) / msPerDay);

                  if (daysDiff === 1) {
                    // Consecutive day - increment streak
                    newCard.lastLoginDate = gameDate;
                    newCard.progress = card.progress + 1;
                    wasUpdated = true;
                  } else if (daysDiff > 1) {
                    // Streak broken - reset
                    newCard.lastLoginDate = gameDate;
                    newCard.progress = 1;
                    wasUpdated = true;
                  }
                  // daysDiff === 0 means same day, no update needed
                }
              } else if (req.action === ACTION_TYPE.NO_WITHDRAWAL) {
                // No-withdrawal streak: handled on login (increment) and withdrawal (break)
                if (actionType === ACTION_TYPE.WITHDRAWAL) {
                  // Streak is broken
                  newCard.streakBroken = true;
                  newCard.progress = 0;
                  wasUpdated = true;
                } else if (actionType === ACTION_TYPE.LOGIN && !newCard.streakBroken) {
                  // Daily increment if streak not broken
                  const createdDate = new Date(newCard.createdDate);
                  const currentDate = new Date(gameDate);
                  const msPerDay = 24 * 60 * 60 * 1000;
                  const daysSinceCreation = Math.floor((currentDate - createdDate) / msPerDay);
                  // Progress is days since card was created (no withdrawals)
                  if (daysSinceCreation > newCard.progress) {
                    newCard.progress = daysSinceCreation;
                    wasUpdated = true;
                  }
                }
              }
              break;

            case REQUIREMENT_TYPE.PASSIVE:
              // Passive cards don't trigger on regular actions
              break;

            default:
              break;
          }

          if (wasUpdated) {
            updated.push(newCard);
            // Check if now completable
            const target = req.target || req.count || 0;
            if (target > 0 && newCard.progress >= target) {
              completable.push(newCard.id);
            }
          }

          return newCard;
        });

        if (updated.length > 0) {
          set({ cards: updatedCards });
        }

        return { updated, completable };
      },

      // Draw System Actions
      drawCard: (rarityFilter = null) => {
        const state = get();

        // Select rarity (filtered or random)
        let rarity;
        if (rarityFilter) {
          // For milestone draws, pick from filtered rarities
          const filteredRarities = Object.keys(CARD_POOL).filter(r =>
            rarityFilter.includes(r) && CARD_POOL[r].length > 0
          );
          if (filteredRarities.length === 0) {
            return { success: false, reason: 'no_cards_available' };
          }
          rarity = filteredRarities[Math.floor(Math.random() * filteredRarities.length)];
        } else {
          rarity = selectRandomRarity();
        }

        // Pick random card from pool
        const pool = CARD_POOL[rarity];
        const template = pool[Math.floor(Math.random() * pool.length)];

        // Create card instance
        const cardInstance = createCardInstance(template, state.gameDate);

        // Add to collection
        const result = get().addCard(cardInstance);
        if (!result.added) {
          return { success: false, reason: result.reason };
        }

        return { success: true, card: { ...cardInstance, isActive: result.isActive } };
      },

      claimDailyDraw: () => {
        const state = get();

        if (state.lastDrawDate === state.gameDate) {
          return { success: false, reason: 'already_drawn' };
        }

        const result = get().drawCard();
        if (result.success) {
          set({ lastDrawDate: state.gameDate });
        }

        return result;
      },

      initializeStarterCards: () => {
        const state = get();

        if (state.cards.length > 0) {
          return { initialized: false, reason: 'not_new_user' };
        }

        const cards = [];
        for (const template of STARTER_CARDS) {
          const cardInstance = createCardInstance(template, state.gameDate);
          // Force active for starter cards
          cardInstance.isActive = true;
          cards.push(cardInstance);
        }

        set({ cards, streakStartDate: state.gameDate });
        return { initialized: true, cards };
      },

      checkMilestoneDraws: () => {
        const state = get();
        const draws = [];
        const newMilestones = [];
        const updates = { ...state.lastMilestoneChecks };

        // Calculate streak days
        let streakDays = 0;
        if (state.streakStartDate) {
          const start = new Date(state.streakStartDate);
          const current = new Date(state.gameDate);
          const msPerDay = 24 * 60 * 60 * 1000;
          streakDays = Math.floor((current - start) / msPerDay);
        }

        // Calculate total savings
        const savingsTotal = state.deposits.reduce((sum, d) => sum + d.amount, 0);

        // Streak milestones
        if (streakDays >= 7 && !updates.day7) {
          const result = get().drawCard([RARITY.RARE, RARITY.EPIC, RARITY.LEGENDARY]);
          if (result.success) {
            draws.push(result.card);
            newMilestones.push('7-day streak');
            updates.day7 = true;
          }
        }

        if (streakDays >= 30 && !updates.day30) {
          const result = get().drawCard([RARITY.EPIC, RARITY.LEGENDARY]);
          if (result.success) {
            draws.push(result.card);
            newMilestones.push('30-day streak');
            updates.day30 = true;
          }
        }

        if (streakDays >= 365 && !updates.day365) {
          const result = get().drawCard([RARITY.LEGENDARY]);
          if (result.success) {
            draws.push(result.card);
            newMilestones.push('365-day streak');
            updates.day365 = true;
          }
        }

        // Savings milestones
        if (savingsTotal >= 100 && !updates.savings100) {
          const result = get().drawCard([RARITY.RARE, RARITY.EPIC, RARITY.LEGENDARY]);
          if (result.success) {
            draws.push(result.card);
            newMilestones.push('$100 saved');
            updates.savings100 = true;
          }
        }

        if (savingsTotal >= 500 && !updates.savings500) {
          const result = get().drawCard([RARITY.EPIC, RARITY.LEGENDARY]);
          if (result.success) {
            draws.push(result.card);
            newMilestones.push('$500 saved');
            updates.savings500 = true;
          }
        }

        if (savingsTotal >= 1000 && !updates.savings1000) {
          const result = get().drawCard([RARITY.EPIC, RARITY.LEGENDARY]);
          if (result.success) {
            draws.push(result.card);
            newMilestones.push('$1000 saved');
            updates.savings1000 = true;
          }
        }

        if (savingsTotal >= 5000 && !updates.savings5000) {
          const result = get().drawCard([RARITY.LEGENDARY]);
          if (result.success) {
            draws.push(result.card);
            newMilestones.push('$5000 saved');
            updates.savings5000 = true;
          }
        }

        if (newMilestones.length > 0) {
          set({ lastMilestoneChecks: updates });
        }

        return { draws, newMilestones };
      },

      // Date Actions
      setGameDate: (date) => set({ gameDate: date }),

      advanceGameDate: (days) => set((state) => {
        const current = new Date(state.gameDate);
        current.setDate(current.getDate() + days);
        return { gameDate: current.toISOString().split('T')[0] };
      }),

      syncWithRealTime: () => set((state) => {
        const today = getTodayDate();
        const lastReal = new Date(state.lastRealDate);
        const now = new Date(today);

        // Calculate days elapsed since last real date
        const msPerDay = 24 * 60 * 60 * 1000;
        const daysElapsed = Math.floor((now - lastReal) / msPerDay);

        if (daysElapsed > 0) {
          const currentGame = new Date(state.gameDate);
          currentGame.setDate(currentGame.getDate() + daysElapsed);
          return {
            gameDate: currentGame.toISOString().split('T')[0],
            lastRealDate: today
          };
        }

        // Update lastRealDate even if no days elapsed
        return { lastRealDate: today };
      }),

      // M Economy Actions
      recordDeposit: (amount) => {
        const mEarned = 10 + Math.floor(amount);
        const state = get();
        const deposit = {
          id: generateId(),
          amount: amount,
          mEarned: mEarned,
          date: state.gameDate
        };
        set({
          mBalance: state.mBalance + mEarned,
          deposits: [...state.deposits, deposit]
        });

        // Process card triggers for deposit
        get().processCardTriggers(ACTION_TYPE.DEPOSIT, { amount });

        return mEarned;
      },

      claimDailyBonus: () => {
        const state = get();
        if (state.lastLoginDate !== state.gameDate) {
          set({
            mBalance: state.mBalance + 2,
            lastLoginDate: state.gameDate
          });

          // Process card triggers for login
          get().processCardTriggers(ACTION_TYPE.LOGIN, {});

          return true;
        }
        return false;
      },

      recordRoundUp: () => {
        set((state) => ({
          mBalance: state.mBalance + 3
        }));

        // Process card triggers for round-up
        get().processCardTriggers(ACTION_TYPE.ROUND_UP, {});

        return 3;
      },

      recordImpulseSave: () => {
        set((state) => ({
          mBalance: state.mBalance + 5
        }));

        // Process card triggers for impulse save
        get().processCardTriggers(ACTION_TYPE.IMPULSE_SAVE, {});

        return 5;
      },

      recordWithdrawal: (depositId, amount) => {
        const state = get();
        const depositIndex = state.deposits.findIndex(d => d.id === depositId);

        if (depositIndex === -1) {
          return 0; // Deposit not found
        }

        // Check for Emergency Armor passive effect before deducting M
        const armorCard = state.cards.find(c =>
          c.isActive &&
          c.requirement?.type === REQUIREMENT_TYPE.PASSIVE &&
          c.requirement?.effect === 'shield-withdrawal'
        );

        if (armorCard) {
          // Shield activates - no M deduction, complete the armor card
          const armorReward = armorCard.reward || 0;

          // Remove the armor card and grant its reward
          set({
            mBalance: state.mBalance + armorReward,
            cards: state.cards.filter(c => c.id !== armorCard.id)
          });

          console.log(`Emergency Armor activated! Shielded ${amount} withdrawal, earned ${armorReward}M`);

          // Still trigger withdrawal for streak-breaking, but no M loss
          get().processCardTriggers(ACTION_TYPE.WITHDRAWAL, { amount });

          return {
            mDeducted: 0,
            shieldUsed: true,
            shieldReward: armorReward
          };
        }

        const deposit = state.deposits[depositIndex];

        // Calculate proportional M to deduct
        const proportion = amount / deposit.amount;
        const mToDeduct = Math.floor(proportion * deposit.mEarned);

        // Calculate new balance (cannot go below 0)
        const newBalance = Math.max(0, state.mBalance - mToDeduct);

        let newDeposits;
        if (amount >= deposit.amount) {
          // Full withdrawal: remove deposit from array
          newDeposits = state.deposits.filter(d => d.id !== depositId);
        } else {
          // Partial withdrawal: update deposit with reduced amount and mEarned
          const remainingAmount = deposit.amount - amount;
          const remainingMEarned = deposit.mEarned - mToDeduct;
          newDeposits = state.deposits.map(d =>
            d.id === depositId
              ? { ...d, amount: remainingAmount, mEarned: remainingMEarned }
              : d
          );
        }

        set({
          mBalance: newBalance,
          deposits: newDeposits
        });

        // Process card triggers for withdrawal (breaks no-withdrawal streaks)
        get().processCardTriggers(ACTION_TYPE.WITHDRAWAL, { amount });

        return mToDeduct;
      },

      // Admin Actions (for testing)
      addM: (amount) => set((state) => ({
        mBalance: state.mBalance + amount
      })),

      setAccountBalance: (accountKey, amount) => {
        const state = get();
        if (!(accountKey in state.accounts)) {
          return { success: false, reason: 'invalid_account' };
        }
        set({
          accounts: { ...state.accounts, [accountKey]: amount }
        });
        return { success: true };
      },

      resetAllData: () => {
        set({
          mBalance: 0,
          deposits: [],
          cards: [],
          housingLevel: 0,
          accounts: { HYSA: 0, fourOhOneK: 0, brokerage: 0 },
          lastDrawDate: null,
          lastLoginDate: null,
          lastMilestoneChecks: {
            day7: false,
            day30: false,
            day365: false,
            savings100: false,
            savings500: false,
            savings1000: false,
            savings5000: false
          },
          streakStartDate: null,
          gameDate: getTodayDate()
        });
        return { success: true };
      },

      grantSpecificCard: (cardId) => {
        const state = get();

        // Find template from all rarities
        let template = null;
        for (const rarity of Object.values(RARITY)) {
          const found = CARD_POOL[rarity]?.find(c => c.cardId === cardId);
          if (found) {
            template = found;
            break;
          }
        }

        if (!template) {
          return { success: false, reason: 'card_not_found' };
        }

        // Create card instance
        const cardInstance = createCardInstance(template, state.gameDate);

        // Add to collection
        const result = get().addCard(cardInstance);
        if (!result.added) {
          return { success: false, reason: result.reason };
        }

        return { success: true, card: { ...cardInstance, isActive: result.isActive } };
      },

      // Housing Actions
      canAffordHousing: (tierLevel) => {
        const state = get();
        const tier = HOUSING_TIERS.find(t => t.id === tierLevel);
        if (!tier) return false;
        return state.mBalance >= tier.cost;
      },

      purchaseHousing: (tierLevel) => {
        const state = get();
        const tier = HOUSING_TIERS.find(t => t.id === tierLevel);

        if (!tier) {
          return { success: false, reason: 'invalid_tier' };
        }

        if (tierLevel <= state.housingLevel) {
          return { success: false, reason: 'already_owned' };
        }

        if (tierLevel !== state.housingLevel + 1) {
          return { success: false, reason: 'must_purchase_next_tier' };
        }

        if (state.mBalance < tier.cost) {
          return { success: false, reason: 'insufficient_funds' };
        }

        set({
          mBalance: state.mBalance - tier.cost,
          housingLevel: tierLevel
        });

        return { success: true, newLevel: tierLevel };
      },
    }),
    {
      name: 'retirem-game',
    }
  )
);
